import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BarChart3 } from 'lucide-react';
import { ProductBrand } from '@/components/brand/ProductBrand';
import { Spinner } from '@/components/ui/Spinner';
import { adminApi, type AdminAccountMetricRow } from '@/api/adminApi';
import { formatCurrencyValue } from '@/api/keywordMetrics';
import { formatMonthLabel, getDefaultMetricsMonth } from '@/api/reportDateRanges';
import { SignOutButton } from '@/components/auth/SignOutButton';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/utils/cn';
import type { MoneyContext } from '@/utils/formatCurrency';
import {
  adminMetricPageTotal,
  adminMetricRowMoney,
  buildAdminMetricSections,
  type AdminMetricSection,
  type AdminMetricSubtotal,
} from '@/pages/admin/adminAccountMetricSections';

function hasActivity(row: AdminAccountMetricRow): boolean {
  return row.cost !== 0 || row.sales_30d !== 0;
}

function moneyContextForSubtotal(
  section: AdminMetricSection,
  subtotal: AdminMetricSubtotal
): MoneyContext {
  return {
    currencyCode: subtotal.currency,
    countryCode: subtotal.currency ? section.code : null,
  };
}

function MetricRegionHeading({ section }: { section: AdminMetricSection }) {
  return (
    <h2 className="flex items-center gap-2 px-1 text-body-md font-medium text-brand-ink">
      {section.code ? (
        <span className="inline-block w-8 shrink-0 leading-none">{section.code}</span>
      ) : null}
      <span className="min-w-0 leading-none">{section.label}</span>
      {section.currency ? (
        <span className="text-body-sm font-medium text-foreground-muted">{section.currency}</span>
      ) : null}
    </h2>
  );
}

function MetricAccountsTable({
  section,
  totalLabel,
}: {
  section: AdminMetricSection;
  totalLabel: string;
}) {
  const subtotal = section.subtotal;
  const subtotalMoney = subtotal ? moneyContextForSubtotal(section, subtotal) : undefined;

  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-surface">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-foreground-muted">
            <th className="px-4 py-3 font-medium">Account</th>
            <th className="px-4 py-3 font-medium">Manager account</th>
            <th className="px-4 py-3 font-medium text-right">Ad spend</th>
            <th className="px-4 py-3 font-medium text-right">Sales</th>
          </tr>
        </thead>
        <tbody>
          {section.items.map((row) => {
            const money = adminMetricRowMoney(row, section.id);
            return (
              <tr key={row.profile_id} className="border-b border-border last:border-0">
                <td className="px-4 py-3">
                  <Link
                    to={`/accounts/${encodeURIComponent(row.profile_id)}/campaigns/dashboard`}
                    className="text-brand hover:underline font-medium"
                  >
                    {row.account_name || row.profile_id}
                  </Link>
                  <div className="text-xs font-mono text-foreground-subtle mt-0.5">{row.profile_id}</div>
                </td>
                <td className="px-4 py-3 text-foreground-muted">
                  {row.manager_account_name || '—'}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatCurrencyValue(row.cost, money)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatCurrencyValue(row.sales_30d, money)}
                </td>
              </tr>
            );
          })}
        </tbody>
        {subtotal && subtotalMoney ? (
          <tfoot>
            <tr className="border-t border-border bg-surface-muted/50 font-semibold">
              <td className="px-4 py-3" colSpan={2}>
                {totalLabel}
              </td>
              <td className="px-4 py-3 text-right tabular-nums">
                {formatCurrencyValue(subtotal.cost, subtotalMoney)}
              </td>
              <td className="px-4 py-3 text-right tabular-nums">
                {formatCurrencyValue(subtotal.sales_30d, subtotalMoney)}
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

export function AdminAccountMetricsPage() {
  const [month, setMonth] = useState(getDefaultMetricsMonth);
  const [hideZeroActivity, setHideZeroActivity] = useState(true);
  const { isSuperAdmin } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-account-metrics', month],
    queryFn: () => adminApi.getAccountMetrics(month),
    enabled: Boolean(month),
  });

  const metrics = data?.data;
  const allRows = metrics?.items ?? [];
  const sections = useMemo(
    () => buildAdminMetricSections(hideZeroActivity ? allRows.filter(hasActivity) : allRows),
    [allRows, hideZeroActivity]
  );
  const pageTotal = useMemo(() => adminMetricPageTotal(sections), [sections]);
  const rows = sections.flatMap((section) => section.items);

  return (
    <div className="min-h-screen bg-page">
      <header className="bg-surface border-b border-border">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <ProductBrand variant="light" showTagline={false} />
            <div className="hidden sm:flex items-center gap-2 border-l border-border pl-4">
              <BarChart3 className="w-5 h-5 text-brand shrink-0" aria-hidden />
              <h1 className="text-headline-md font-semibold text-foreground">Account metrics</h1>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            {isSuperAdmin ? (
              <>
                <Link
                  to="/admin/report-coverage"
                  className="text-body-sm text-brand hover:underline font-medium"
                >
                  Report coverage
                </Link>
                <Link
                  to="/admin/stream-subscriptions"
                  className="text-body-sm text-brand hover:underline font-medium"
                >
                  Stream subscriptions
                </Link>
              </>
            ) : null}
            <Link
              to="/admin/users"
              className="text-body-sm text-brand hover:underline font-medium"
            >
              Users
            </Link>
            <Link to="/accounts" className="text-body-sm text-foreground-muted hover:text-foreground">
              Accounts
            </Link>
            <SignOutButton />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-foreground-muted mb-1">
              Monthly ad spend and ad-attributed sales across all advertising accounts.
            </p>
            {metrics?.range ? (
              <p className="text-sm text-foreground-subtle">
                {formatMonthLabel(month)} ({metrics.range.startDate} – {metrics.range.endDate})
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-2 pb-2 text-sm">
              <button
                id="hide-zero-activity"
                type="button"
                role="switch"
                aria-checked={hideZeroActivity}
                onClick={() => setHideZeroActivity((current) => !current)}
                className={cn(
                  'relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors',
                  hideZeroActivity ? 'bg-brand' : 'bg-border'
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-surface shadow transition-transform',
                    hideZeroActivity && 'translate-x-4'
                  )}
                />
              </button>
              <label htmlFor="hide-zero-activity" className="cursor-pointer font-medium text-foreground">
                Hide accounts with no activity
              </label>
            </div>
            <label className="block text-sm">
              <span className="font-medium text-foreground">Month</span>
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="mt-1 block rounded-lg border border-border px-3 py-2 text-sm"
              />
            </label>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner className="w-10 h-10" />
          </div>
        ) : error ? (
          <p className="text-error">{(error as Error).message}</p>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-border bg-surface px-4 py-8 text-center text-sm text-foreground-muted">
            {hideZeroActivity && allRows.length > 0
              ? 'No accounts with spend or sales for this month.'
              : 'No accounts found.'}
          </div>
        ) : (
          <div className="space-y-6">
            {sections.map((section) => (
              <section key={section.id} className="space-y-2">
                <MetricRegionHeading section={section} />
                <MetricAccountsTable
                  section={section}
                  totalLabel={sections.length > 1 ? 'Subtotal' : 'Total'}
                />
              </section>
            ))}
            {pageTotal && sections.length > 1 ? (
              <div className="overflow-x-auto rounded-xl border border-border bg-surface">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="font-semibold">
                      <td className="px-4 py-3" colSpan={2}>
                        Total
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatCurrencyValue(pageTotal.cost, { currencyCode: pageTotal.currency })}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatCurrencyValue(pageTotal.sales_30d, {
                          currencyCode: pageTotal.currency,
                        })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        )}
      </main>
    </div>
  );
}
